// Synthetic vibration data for S5 Signal Analysis. The real
// implementation will fetch from the sensor-graph-service via
// the typed client; these fixtures keep the layout and visuals
// honest until that endpoint lands.
//
// Per-asset variation: the same asset id always produces the same
// fixture (deterministic from a 32-bit hash of the id). This way the
// S5 picker actually visibly changes the panels when an operator
// switches assets, instead of every asset looking identical.

import type { StatusLevel } from '@/lib/StatusPill';

export type ScalarSeries = {
  /** Display name (e.g. "RMS"). */
  metric: 'RMS' | 'Peak' | 'Crest' | 'Kurtosis';
  /** Engineering unit string (e.g. "mm/s", "g", "—"). */
  unit: string;
  /** Time (Unix seconds) + scalar value, sorted ascending by t. */
  points: Array<{ t: number; v: number }>;
  /** Latest reading carried as a chip next to the track label. */
  last: { value: number; tone: StatusLevel };
};

export type FFTThumbnail = {
  /** Capture time. */
  at: Date;
  /** 16 amplitude bins — enough resolution for a visual fingerprint. */
  bins: number[];
  severity: StatusLevel;
};

export type Annotation = {
  /** ISO date string at the annotation's time. */
  atSec: number;
  /** Short label rendered as a pill. */
  label: string;
  /** Tone — drives the pill color. */
  tone: StatusLevel;
};

export type SensorInfo = {
  id: string;
  /** Operator-readable name, e.g. "Bearing accelerometer · Z". */
  name: string;
  /** Physical position on the asset for the topology hint. */
  position: 'drive_end' | 'non_drive_end' | 'casing' | 'inboard';
  /** Sample rate of the raw waveform in Hz (informational). */
  sampleRateHz: number;
  /** Cadence at which the sensor uploads aggregated scalars + FFTs. */
  uploadEverySec: number;
};

export type FaultMarkerWithAmplitude = {
  hz: number;
  label: string;
  tone: 'critical' | 'warn' | 'watch' | 'info';
  /** Current amplitude at this frequency (engineering units, e.g. g rms). */
  amplitude: number;
  /** Multiplier vs. baseline amplitude (e.g. 4.2 means 4.2× baseline). */
  ratio: number;
  /** Direction over the trailing window. */
  trend: 'rising' | 'stable' | 'falling';
  /** Multi-bin series for the trend sparkline. */
  series: number[];
};

export type AnalysisFixture = {
  sensorId: string;
  sensorName: string;
  assetId: string;
  assetName: string;
  sampleRate: number;
  waveform: number[];
  spectrum: { amplitudes: number[]; maxHz: number };
  baselineSpectrum: number[];
  spectrogramGrid: number[][];
  spectrogramDurationSec: number;
  bearingFaultMarkers: FaultMarkerWithAmplitude[];
  /** Engineering scalars over the trailing 14d, one entry per metric. */
  scalars: ScalarSeries[];
  /** 24 mini-FFTs across the trailing 14d window for the history strip. */
  fftThumbnails: FFTThumbnail[];
  /** Auto-detected events placed on the time axis. */
  annotations: Annotation[];
  /** Sensors attached to this asset. The fixture's primary scalars
   *  + waveform + FFT correspond to `sensors[0]`. */
  sensors: SensorInfo[];
  /** Saved baseline captures available for the operator to compare
   *  the current FFT against. Three are seeded per asset spanning
   *  the trailing 90 days. The current selection lives on the URL
   *  (?baseline=) — these are the menu options. */
  baselineCaptures: BaselineSummary[];
};

export type BaselineSummary = {
  id: string;
  label: string;
  atSec: number;
  note?: string;
};

// Demo catalog — minimal fleet roster for the synthetic case. Asset
// ids match the api-gateway in-memory fleet so the picker → fixture
// hand-off stays coherent even when the real endpoint isn't mocked.
//
// `kind` drives which fault frequencies are emitted on the bearing
// tracker. Rolling-element bearings (reb) carry the classic
// BPFI/BPFO/BSF/FTF set; gearboxes carry gear-mesh frequency;
// belt-driven assets carry belt-pass frequency; sleeve-bearing
// equipment carries oil-whirl (~0.43×RPM). See the BPFI/BPFO design
// note for the reasoning — blanket BPFI on a gearbox is a lie.
export type AssetKind = 'reb' | 'sleeve' | 'gearbox' | 'belt' | 'recip';

type AssetMeta = {
  name: string;
  runHz: number;
  /** BPFI Hz when kind=reb. Ignored otherwise. */
  bpfiHz: number;
  /** BPFO Hz when kind=reb. Ignored otherwise. */
  bpfoHz: number;
  tone: FaultMarkerWithAmplitude['tone'];
  photo: string;
  kind: AssetKind;
  /** Class label for the picker / fleet card. */
  klass: 'Pump' | 'Motor' | 'Compressor' | 'Fan' | 'Gearbox';
};
const ASSET_CATALOG: Record<string, AssetMeta> = {
  // Two critical assets so the multi-critical Attention strip is
  // demonstrable. asset_2 was warn previously; promoted to critical
  // with a different fault signature (BPFO-dominant) so the demo
  // shows two distinct critical narratives, not one duplicated.
  asset_1: { name: 'Cooling pump 204', runHz: 60, bpfiHz: 185, bpfoHz: 305, tone: 'critical', photo: '/assets/photos/motor-01.jpg', kind: 'reb',     klass: 'Pump' },
  asset_2: { name: 'Booster pump 31B', runHz: 50, bpfiHz: 152, bpfoHz: 248, tone: 'critical', photo: '/assets/photos/motor-02.jpg', kind: 'reb',     klass: 'Pump' },
  asset_3: { name: 'Drive motor M-44', runHz: 30, bpfiHz: 92,  bpfoHz: 148, tone: 'watch',    photo: '/assets/photos/motor-03.jpg', kind: 'reb',     klass: 'Motor' },
  // Compressor on sleeve bearings — no BPFI; carries 1×RPM + oil
  // whirl (~0.43×RPM). Different fault dictionary, different chart.
  asset_4: { name: 'Compressor C-8B',  runHz: 25, bpfiHz: 78,  bpfoHz: 124, tone: 'warn',     photo: '/assets/photos/motor-04.jpg', kind: 'sleeve',  klass: 'Compressor' },
  asset_5: { name: 'Cooling pump 207', runHz: 60, bpfiHz: 178, bpfoHz: 292, tone: 'watch',    photo: '/assets/photos/motor-05.jpg', kind: 'reb',     klass: 'Pump' },
  // Belt-driven booster pump — fault signature is belt-pass frequency
  // (BPF ≈ 1/3 of run speed for a typical pulley ratio).
  asset_6: { name: 'Booster pump 31A', runHz: 50, bpfiHz: 148, bpfoHz: 244, tone: 'watch',    photo: '/assets/photos/motor-01.jpg', kind: 'belt',    klass: 'Pump' },
  // Gearbox — mesh frequency = teeth × shaft RPM. 30 Hz × 17 teeth
  // = 510 Hz, just out of the demo's 0–500 Hz window, so use a
  // smaller pinion (8 teeth) → 240 Hz GMF.
  asset_7: { name: 'Mixer gearbox G-3', runHz: 30, bpfiHz: 89,  bpfoHz: 144, tone: 'info',     photo: '/assets/photos/motor-03.jpg', kind: 'gearbox', klass: 'Gearbox' },
};

/** Public URL of the asset's photo, or null if the catalog has no
 *  entry. Components should still degrade gracefully if the file
 *  itself is missing (use <AssetPhoto> wrapper). */
export function photoForAsset(assetId: string | undefined): string | null {
  if (!isKnownAsset(assetId)) return null;
  return ASSET_CATALOG[assetId].photo;
}

export const KNOWN_ASSET_IDS = Object.keys(ASSET_CATALOG);

export function isKnownAsset(id: string | undefined): id is string {
  return id != null && id in ASSET_CATALOG;
}

// Mulberry32 — small deterministic PRNG seeded from a 32-bit int.
// We use the asset-id hash so re-rendering the same asset produces
// the exact same chart shape (no flicker on navigation).
function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash32(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function sine(n: number, freq: number, sampleRate: number, amp: number, noiseAmp: number, rnd: () => number) {
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    out[i] = amp * Math.sin(2 * Math.PI * freq * t) + (rnd() - 0.5) * noiseAmp;
  }
  return out;
}

function spectrum(n: number, peaks: Array<{ at: number; gain: number }>, rnd: () => number) {
  const out = new Array<number>(n).fill(0).map(() => rnd() * 0.04);
  for (const p of peaks) {
    const idx = Math.round(p.at * n);
    for (let i = -3; i <= 3; i++) {
      const j = idx + i;
      if (j >= 0 && j < n) {
        out[j] = Math.max(out[j], p.gain * Math.exp(-(i * i) / 1.2));
      }
    }
  }
  return out;
}

function spectrogramGrid(tBins: number, fBins: number, bandFractions: number[], rnd: () => number) {
  const out: number[][] = [];
  for (let t = 0; t < tBins; t++) {
    const ramp = t / tBins;
    const col = new Array<number>(fBins);
    for (let f = 0; f < fBins; f++) {
      const noise = 0.05 + rnd() * 0.05;
      let cell = noise;
      for (let b = 0; b < bandFractions.length; b++) {
        const bandPos = bandFractions[b];
        const intensity = b === 0 ? 0.4 : 0.2 + 0.8 * ramp;
        cell += Math.exp(-Math.pow(f / fBins - bandPos, 2) / 0.001) * intensity;
      }
      col[f] = cell;
    }
    out.push(col);
  }
  return out;
}

// 14d × 48 samples-per-day (30-min cadence) = 672 readings — enough
// for a trend track without burning the main thread on per-point
// renders. uPlot eats this in <5ms.
const SCALAR_SAMPLES = 14 * 48;
const SCALAR_SAMPLE_INTERVAL_SEC = 30 * 60;

function buildScalars(rnd: () => number, severity: FaultMarkerWithAmplitude['tone']): ScalarSeries[] {
  // Each metric trends differently depending on the asset's severity.
  // Critical assets show a ramping RMS + spiking kurtosis; healthy
  // assets stay roughly flat with sensor noise.
  const nowSec = Math.floor(Date.now() / 1000);
  const startSec = nowSec - SCALAR_SAMPLES * SCALAR_SAMPLE_INTERVAL_SEC;
  // Ramp factor — how aggressively the metrics drift toward the end
  // of the window. 0 = flat, 1 = full doubling by the right edge.
  const ramp = severity === 'critical' ? 0.9
    : severity === 'warn' ? 0.5
    : severity === 'watch' ? 0.25
    : 0.05;

  function build(metric: ScalarSeries['metric'], base: number, jitter: number, unit: string): ScalarSeries {
    const points: ScalarSeries['points'] = [];
    for (let i = 0; i < SCALAR_SAMPLES; i++) {
      const t = startSec + i * SCALAR_SAMPLE_INTERVAL_SEC;
      const frac = i / SCALAR_SAMPLES;
      const trend = 1 + ramp * frac;
      const noise = (rnd() - 0.5) * jitter;
      points.push({ t, v: base * trend + noise });
    }
    const last = points[points.length - 1].v;
    const first = points[0].v;
    const ratio = last / Math.max(1e-6, first);
    const tone: StatusLevel = ratio > 1.8 ? 'critical'
      : ratio > 1.4 ? 'warn'
      : ratio > 1.15 ? 'watch'
      : 'healthy';
    return { metric, unit, points, last: { value: last, tone } };
  }

  return [
    build('RMS', 0.42, 0.04, 'g'),
    build('Peak', 1.2, 0.15, 'g'),
    build('Crest', 3.0, 0.4, '—'),
    build('Kurtosis', 3.1, 0.35, '—'),
  ];
}

function buildThumbnails(rnd: () => number, severity: FaultMarkerWithAmplitude['tone'], bpfiBin: number): FFTThumbnail[] {
  // 24 thumbnails over a 14d window — roughly one every 14h. Severity
  // climbs across the window so the early thumbnails read healthy and
  // the late ones critical; matches what an operator sees when they
  // scrub backwards from "now" looking for the first signs of trouble.
  const out: FFTThumbnail[] = [];
  const nowSec = Math.floor(Date.now() / 1000);
  for (let i = 0; i < 24; i++) {
    const frac = i / 23;
    const at = new Date((nowSec - (23 - i) * 14 * 3600) * 1000);
    const bpfiGain = 0.3 + frac * (severity === 'critical' ? 1.6 : severity === 'warn' ? 0.9 : 0.5);
    const bins = new Array<number>(16).fill(0).map(() => 0.05 + rnd() * 0.1);
    // Inject the running-speed bin + a fault bin so the thumbnail
    // visually evolves over time — the right-edge thumbnails should
    // look "spiky" at the fault frequency.
    bins[1] = Math.max(bins[1], 0.4);
    bins[Math.min(15, bpfiBin)] = Math.max(bins[Math.min(15, bpfiBin)], bpfiGain);
    const sev: StatusLevel = bpfiGain > 1.2 ? 'critical'
      : bpfiGain > 0.8 ? 'warn'
      : bpfiGain > 0.5 ? 'watch'
      : 'healthy';
    out.push({ at, bins, severity: sev });
  }
  return out;
}

function buildAnnotations(
  severity: FaultMarkerWithAmplitude['tone'],
  kind: AssetKind,
): Annotation[] {
  // Two synthetic anomaly markers on the trailing window. Real
  // implementation would come from the anomaly-detection service.
  // The annotations stamp times relative to "now" so they land in
  // plausible positions on the time axis regardless of when the
  // page is opened. Annotation labels vary by asset kind so the
  // story matches the markers ("GMF emerges" on a gearbox, not BPFI).
  const nowSec = Math.floor(Date.now() / 1000);
  if (severity === 'info') return [];
  const dominantLabel = kind === 'gearbox' ? 'GMF emerges'
    : kind === 'belt' ? 'BPF emerges'
    : kind === 'sleeve' ? 'Oil whirl emerges'
    : 'BPFI emerges';
  const tone = severity === 'critical' ? 'critical' : 'warn';
  return [
    { atSec: nowSec - 4 * 24 * 3600, label: dominantLabel, tone },
    { atSec: nowSec - 1 * 24 * 3600, label: 'RMS breach', tone },
  ];
}

/**
 * Pick the asset's "dominant" fault frequency — the one the FFT and
 * spectrogram should visually pin to. For REB assets the BPFI is the
 * usual offender; sleeve bearings have no BPFI, so we use 1×RPM.
 * Gearboxes use the gear-mesh frequency; belt-driven assets use the
 * belt-pass frequency. The number returned should always fall inside
 * the 0..maxHz window the spectrum renders.
 */
function dominantFaultHz(cat: AssetMeta): number {
  switch (cat.kind) {
    case 'reb':
      return cat.bpfiHz;
    case 'gearbox':
      // Gear-mesh frequency = teeth × shaft RPM. 8-tooth pinion is a
      // sensible demo default; lands within 0..500 Hz for runHz<=62.
      return cat.runHz * 8;
    case 'belt':
      // Belt-pass frequency ≈ 1/3 of shaft RPM for typical pulley
      // ratios. Real BPF = pulley diameter × π / belt length × RPM,
      // but the operator picks this number from the catalog.
      return cat.runHz * 0.33;
    case 'sleeve':
      // Oil whirl ≈ 0.43×RPM is the classic sleeve-bearing fault. We
      // surface that AND 1×RPM as the dominant carrier.
      return cat.runHz;
    case 'recip':
      // Compression/firing frequency ≈ 2× crankshaft RPM for a 4-cyl
      // double-acting machine — placeholder until any recip asset
      // joins the catalog.
      return cat.runHz * 2;
  }
}

/**
 * Build the fault-frequency rows for the bearing tracker table. Set
 * varies by asset kind because BPFI/BPFO are bearing-specific — see
 * the BPFI/BPFO design note. Severity tones cascade from the asset's
 * top-level tone so the dominant fault inherits the asset's severity.
 */
function buildFaultMarkers(
  cat: AssetMeta,
  rnd: () => number,
  dominantGain: number,
  baselineDominant: number,
): FaultMarkerWithAmplitude[] {
  const tone = cat.tone;
  // The "rising" trend tracks where the asset's narrative is — every
  // tone above info implies a rising primary fault. Secondary
  // frequencies stay stable so the operator sees a single clear
  // signal, not a fog of false alarms.
  const dominantSeries = rampSeries(rnd, tone === 'critical' ? 1.4 : tone === 'warn' ? 0.8 : tone === 'watch' ? 0.4 : 0.1);
  const calmSeries = (gain: number) => rampSeries(rnd, gain);
  const oneRPM: FaultMarkerWithAmplitude = {
    hz: cat.runHz,
    label: '1×RPM',
    tone: 'info',
    amplitude: 0.22,
    ratio: 1.05,
    trend: 'stable',
    series: calmSeries(0.05),
  };
  const twoRPM: FaultMarkerWithAmplitude = {
    hz: cat.runHz * 2,
    label: '2×RPM',
    tone: 'info',
    amplitude: 0.11,
    ratio: 1.0,
    trend: 'stable',
    series: calmSeries(0.04),
  };

  switch (cat.kind) {
    case 'reb': {
      // Cage = FTF ≈ 0.4×RPM, ball spin = BSF ≈ 2.5×RPM (rough).
      return [
        oneRPM,
        twoRPM,
        {
          hz: cat.bpfiHz,
          label: 'BPFI',
          tone,
          amplitude: round2(dominantGain * 0.5),
          ratio: round1(dominantGain / baselineDominant),
          trend: tone === 'info' ? 'stable' : 'rising',
          series: dominantSeries,
        },
        {
          hz: cat.bpfoHz,
          label: 'BPFO',
          tone: tone === 'critical' ? 'warn' : 'watch',
          amplitude: 0.18,
          ratio: 1.4,
          trend: tone === 'critical' ? 'rising' : 'stable',
          series: calmSeries(0.3),
        },
        {
          hz: cat.runHz * 2.5,
          label: 'BSF',
          tone: 'watch',
          amplitude: 0.09,
          ratio: 1.1,
          trend: 'rising',
          series: calmSeries(0.15),
        },
        {
          hz: cat.runHz * 0.4,
          label: 'FTF',
          tone: 'info',
          amplitude: 0.04,
          ratio: 1.0,
          trend: 'stable',
          series: calmSeries(0.05),
        },
      ];
    }
    case 'sleeve': {
      // No BPFI/BPFO — sleeve bearings have no rolling elements. Oil
      // whirl + half-frequency whip are the diagnostic markers.
      return [
        oneRPM,
        twoRPM,
        {
          hz: cat.runHz * 0.43,
          label: 'Oil whirl',
          tone,
          amplitude: round2(dominantGain * 0.4),
          ratio: round1(dominantGain / baselineDominant),
          trend: tone === 'info' ? 'stable' : 'rising',
          series: dominantSeries,
        },
        {
          hz: cat.runHz * 0.48,
          label: 'Oil whip',
          tone: 'watch',
          amplitude: 0.07,
          ratio: 1.05,
          trend: 'stable',
          series: calmSeries(0.08),
        },
      ];
    }
    case 'gearbox': {
      // Gear-mesh fundamental + sideband. Sidebands at GMF ± 1×RPM
      // distinguish runout (low sideband) from tooth fault (high).
      const gmf = cat.runHz * 8;
      return [
        oneRPM,
        twoRPM,
        {
          hz: gmf,
          label: 'GMF',
          tone,
          amplitude: round2(dominantGain * 0.5),
          ratio: round1(dominantGain / baselineDominant),
          trend: tone === 'info' ? 'stable' : 'rising',
          series: dominantSeries,
        },
        {
          hz: gmf + cat.runHz,
          label: 'GMF +1×',
          tone: 'watch',
          amplitude: 0.08,
          ratio: 1.1,
          trend: 'stable',
          series: calmSeries(0.1),
        },
        {
          hz: gmf - cat.runHz,
          label: 'GMF −1×',
          tone: 'watch',
          amplitude: 0.07,
          ratio: 1.05,
          trend: 'stable',
          series: calmSeries(0.08),
        },
      ];
    }
    case 'belt': {
      const bpf = cat.runHz * 0.33;
      return [
        oneRPM,
        twoRPM,
        {
          hz: bpf,
          label: 'BPF',
          tone,
          amplitude: round2(dominantGain * 0.45),
          ratio: round1(dominantGain / baselineDominant),
          trend: tone === 'info' ? 'stable' : 'rising',
          series: dominantSeries,
        },
        {
          hz: bpf * 2,
          label: '2×BPF',
          tone: 'watch',
          amplitude: 0.08,
          ratio: 1.1,
          trend: 'stable',
          series: calmSeries(0.1),
        },
      ];
    }
    case 'recip': {
      return [oneRPM, twoRPM];
    }
  }
}

function buildSensors(assetId: string): SensorInfo[] {
  // Two sensors on every motor/pump — drive-end and non-drive-end —
  // because that's the minimum sensible coverage for rolling-element
  // diagnostics. Cadence is 30 min default; matches what the operator
  // reported as their typical configuration. Sample rate is 25.6 kHz
  // (Nyquist 12.8 kHz) which is enough for the 0..500 Hz band the
  // demo focuses on.
  const num = assetId.split('_')[1] ?? '1';
  return [
    {
      id: `sensor_${num}_de`,
      name: 'Drive-end accelerometer · Z',
      position: 'drive_end',
      sampleRateHz: 25_600,
      uploadEverySec: 30 * 60,
    },
    {
      id: `sensor_${num}_nde`,
      name: 'Non-drive-end accelerometer · Z',
      position: 'non_drive_end',
      sampleRateHz: 25_600,
      uploadEverySec: 30 * 60,
    },
  ];
}

export function buildFixture(assetId?: string): AnalysisFixture {
  const id = isKnownAsset(assetId) ? assetId : KNOWN_ASSET_IDS[0];
  const cat = ASSET_CATALOG[id];
  const rnd = mulberry32(hash32(id));

  // 2048 samples × 25.6 kHz = 80 ms window — enough for a few cycles
  // of the dominant running-speed frequency.
  const sampleRate = 25_600;
  const waveform = sine(2048, cat.runHz, sampleRate, 0.8, 0.2, rnd);

  // Pick the "dominant" fault Hz per asset kind. The spectrum + the
  // spectrogram both highlight this band so the cross-panel alignment
  // stays honest (band on the spectrogram == peak on the spectrum).
  const maxHz = 500;
  const peakAt = (hz: number) => hz / maxHz;
  const dominantHz = dominantFaultHz(cat);

  const dominantGain = cat.tone === 'critical' ? 1.6 : cat.tone === 'warn' ? 1.1 : cat.tone === 'watch' ? 0.7 : 0.4;
  const baselineDominant = 0.38;
  const spec = spectrum(256, [
    { at: peakAt(cat.runHz), gain: 0.5 },
    { at: peakAt(dominantHz), gain: dominantGain },
  ], rnd);
  const baseline = spectrum(256, [
    { at: peakAt(cat.runHz), gain: 0.45 },
    { at: peakAt(dominantHz), gain: baselineDominant },
  ], rnd);

  // Spectrogram bands sit at the same relative frequency as the
  // FFT peaks, so the cross-panel alignment the user sees in the
  // analyze view is real (the band on the spectrogram lines up with
  // the dominant peak on the spectrum below).
  const grid = spectrogramGrid(48, 36, [peakAt(cat.runHz), peakAt(dominantHz)], rnd);

  const sensors = buildSensors(id);
  const scalars = buildScalars(rnd, cat.tone);
  const fftThumbnails = buildThumbnails(rnd, cat.tone, Math.round(peakAt(dominantHz) * 16));
  const annotations = buildAnnotations(cat.tone, cat.kind);
  const baselineCaptures = buildBaselineCaptures(id);

  // Bearing-fault markers vary by asset kind so the Frequency
  // Intelligence table tells the right diagnostic story:
  // rolling-element bearings → BPFI/BPFO/BSF/FTF; sleeve →
  // oil-whirl + 1×/2×; gearbox → GMF + sidebands; belt → BPF.
  const bearingFaultMarkers = buildFaultMarkers(cat, rnd, dominantGain, baselineDominant);

  return {
    sensorId: sensors[0].id,
    sensorName: sensors[0].name,
    assetId: id,
    assetName: cat.name,
    sampleRate,
    waveform,
    spectrum: { amplitudes: spec, maxHz },
    baselineSpectrum: baseline,
    spectrogramGrid: grid,
    spectrogramDurationSec: 12,
    bearingFaultMarkers,
    scalars,
    fftThumbnails,
    annotations,
    sensors,
    baselineCaptures,
  };
}

function buildBaselineCaptures(assetId: string): BaselineSummary[] {
  // Three snapshots spanning the last 90 days — one fresh "healthy"
  // capture, one after commissioning (or last service), and the
  // original factory baseline. Operator picks which one to compare
  // against in ComparisonOverlay.
  const nowSec = Math.floor(Date.now() / 1000);
  return [
    {
      id: `${assetId}_baseline_recent`,
      label: 'Healthy · last week',
      atSec: nowSec - 7 * 24 * 3600,
      note: 'Captured after May service window.',
    },
    {
      id: `${assetId}_baseline_postservice`,
      label: 'Post-service · 30d ago',
      atSec: nowSec - 30 * 24 * 3600,
      note: 'Captured after bearing reinstall.',
    },
    {
      id: `${assetId}_baseline_factory`,
      label: 'Factory · commissioning',
      atSec: nowSec - 84 * 24 * 3600,
      note: 'Initial OEM signature.',
    },
  ];
}

function rampSeries(rnd: () => number, ramp: number): number[] {
  // 12-point sparkline series with a configurable upward ramp + noise.
  const out: number[] = [];
  for (let i = 0; i < 12; i++) {
    const t = i / 11;
    out.push(0.4 + t * ramp + (rnd() - 0.5) * 0.06);
  }
  return out;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Lightweight asset summary the S5 picker renders. Source-of-truth
 * for the picker today is this catalog; a follow-up swaps it for
 * `GET /v1/assets` via openapi-fetch.
 */
export type AssetOption = {
  id: string;
  name: string;
  site: string;
  klass: AssetMeta['klass'];
  kind: AssetKind;
};

export function listAssetOptions(): AssetOption[] {
  return KNOWN_ASSET_IDS.map((id) => ({
    id,
    name: ASSET_CATALOG[id].name,
    site: 'Plant A',
    klass: ASSET_CATALOG[id].klass,
    kind: ASSET_CATALOG[id].kind,
  }));
}

/** Asset metadata exposed for callers that need to render a chip
 *  with klass/kind, e.g. the sensor detail screen or the fleet card
 *  badge. Keep it read-only — direct catalog mutation is not part
 *  of the public surface. */
export function getAssetMeta(assetId: string | undefined): {
  klass: AssetMeta['klass'];
  kind: AssetKind;
  runHz: number;
} | null {
  if (!isKnownAsset(assetId)) return null;
  const m = ASSET_CATALOG[assetId];
  return { klass: m.klass, kind: m.kind, runHz: m.runHz };
}
